import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Container,
  Stack,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
  alpha,
  Divider,
  Popper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Modal,
  useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import svgPaths from "./svg-paths";
import toolsSvgPaths from "./svg-tools-paths";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.getevo.app";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#07001a" },
    secondary: { main: "#16A34A" },
    background: { default: "#f8f7f6", paper: "#FFFFFF" },
    text: { primary: "#07001a", secondary: "#776f7b" },
  },
  typography: {
    fontFamily:
      "'Figtree', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: "#f8f7f6" } },
    },
  },
});

const BorderColor = "rgba(0, 6, 20, 0.08)";

const GRADING_PROVIDERS = [
  { key: "psa", label: "PSA", field: "grade_ten_price" as const },
  { key: "bgs", label: "BGS", field: "bgs_grade_ten_price" as const },
  { key: "cgc", label: "CGC", field: "cgc_grade_ten_price" as const },
  { key: "sgc", label: "SGC", field: "sgc_grade_ten_price" as const },
];

const VARIANT_LABELS: Record<string, string> = {
  normal: "Normal",
  holo: "Holo",
  reverse_holo: "Reverse Holo",
  first_edition: "1st Edition",
  poke_ball: "Poké Ball",
  master_ball: "Master Ball",
};

const PRESET_FEES = [
  { label: "$24.99", value: 24.99 },
  { label: "$32.99", value: 32.99 },
  { label: "$49.99", value: 49.99 },
];

interface ArbitrageCard {
  id: string;
  name: string;
  image: string;
  set_name: string;
  series_name: string;
  ungraded_price: number;
  grade_nine_price: number | null;
  grade_ten_price: number;
  bgs_grade_ten_price: number | null;
  cgc_grade_ten_price: number | null;
  sgc_grade_ten_price: number | null;
  spread: number;
  number: string;
  variant: string;
  language: string;
  tcg_player_id: number;
  release_date: string;
  sales_volume: number;
}

interface ArbitrageResponse {
  cards: ArbitrageCard[];
  total: number;
  limit: number;
  offset: number;
  min_volume: number;
  max_volume: number;
}

interface OptionItem {
  name: string;
  image: string;
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

const hasImageExtension = (url: string) =>
  /\.(?:webp|png|jpe?g)(?:[?#].*)?$/i.test(url.trim());

const cardImageUrls = (image: string) => {
  const trimmed = image.trim();
  if (!trimmed || hasImageExtension(trimmed)) {
    return { display: trimmed, high: trimmed };
  }
  return {
    display: `${trimmed}_small.webp`,
    high: `${trimmed}.webp`,
  };
};

const TOP_PLAYS_DEFAULTS = {
  minUngraded: "20",
  maxUngraded: "400",
  minReleaseYear: "2021",
  maxReleaseYear: "",
  minVolumeFilter: 2,
};

function LowRiskBadge() {
  return (
    <Chip
      size="small"
      icon={<ShieldOutlinedIcon />}
      label="Low risk"
      sx={{
        height: 18,
        bgcolor: alpha("#16A34A", 0.12),
        color: "secondary.main",
        fontWeight: 700,
        fontSize: 9,
        "& .MuiChip-icon": { color: "secondary.main", fontSize: 12, ml: 0.5 },
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

const LIMIT = 50;
const APP_VOLUME_BASELINE_SIZE = 20;
const DEBOUNCE_MS = 350;

const createVolumeLevelGetter = (cards: ArbitrageCard[]) => {
  const volumes = cards
    .map((card) => card.sales_volume || 0)
    .filter((volume) => volume > 0)
    .sort((a, b) => a - b);

  if (volumes.length === 0) return () => 0;

  const percentile = (quantile: number) => {
    const position = quantile * (volumes.length - 1);
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    if (lower === upper) return volumes[lower];
    return (
      volumes[lower] +
      (volumes[upper] - volumes[lower]) * (position - lower)
    );
  };

  const p25 = percentile(0.25);
  const p50 = percentile(0.5);
  const p75 = percentile(0.75);

  return (volume: number) => {
    if (!volume || volume <= 0) return 0;
    if (volume <= p25) return 1;
    if (volume <= p50) return 2;
    if (volume <= p75) return 3;
    return 4;
  };
};

const skelKeyframes = `@keyframes skeletonPulse { 0% { opacity: 0.15; } 50% { opacity: 0.08; } 100% { opacity: 0.15; } }`;

const skel = {
  bgcolor: "#07001a",
  borderRadius: "4px",
  animation: "skeletonPulse 1.5s ease-in-out infinite",
} as const;

function SkeletonPlayRows() {
  return (
    <>
      <style>{skelKeyframes}</style>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${BorderColor}`,
          borderRadius: "12px",
          px: { xs: 1.5, sm: 2 },
          bgcolor: "white",
          overflow: "hidden",
        }}
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              py: 2,
              borderBottom: i === 11 ? "none" : `1px solid ${BorderColor}`,
            }}
          >
            <Stack direction="row" spacing={1.5}>
              <Box
                sx={{
                  ...skel,
                  width: { xs: 64, sm: 72 },
                  aspectRatio: "5 / 7",
                  borderRadius: "4px",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        ...skel,
                        width: 150 + (i % 4) * 24,
                        maxWidth: "80%",
                        height: 16,
                        mb: 0.5,
                      }}
                    />
                    <Box
                      sx={{
                        ...skel,
                        width: 90 + (i % 3) * 15,
                        maxWidth: "70%",
                        height: 11,
                        mb: 0.25,
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Box sx={{ ...skel, width: 72, height: 11, mb: 0.5 }} />
                    <Box sx={{ ...skel, width: 58, height: 18, ml: "auto" }} />
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 2 }}
                >
                  {[0, 1, 2].map((column) => (
                    <Box key={column} sx={{ width: "30%" }}>
                      <Box
                        sx={{ ...skel, width: "70%", height: 10, mb: 0.5 }}
                      />
                      <Box sx={{ ...skel, width: "90%", height: 16 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        ))}
      </Paper>
    </>
  );
}

function tcgPlayerAffiliateUrl(card: ArbitrageCard) {
  const printing =
    card.variant === "holo"
      ? "&Printing=Holofoil"
      : card.variant === "reverse_holo"
        ? "&Printing=Reverse+Holofoil"
        : card.variant === "first_edition"
          ? "&Printing=1st+Edition"
          : "";
  const productUrl = `https://www.tcgplayer.com/product/${card.tcg_player_id}?Condition=Near+Mint${printing}`;
  return `https://partner.tcgplayer.com/rEaJxQ?u=${encodeURIComponent(productUrl)}`;
}

function signedMoney(value: number | null) {
  if (value == null) return "–";
  return `${value > 0 ? "+" : ""}${fmt(value)}`;
}

interface PlayRowProps {
  card: ArbitrageCard;
  gradedPrice: number | null;
  graderLabel: string;
  gradingFee: number;
  onImageClick: (url: string) => void;
}

function PlayRow({
  card,
  gradedPrice,
  graderLabel,
  gradingFee,
  onImageClick,
}: PlayRowProps) {
  const gradeNineProfit =
    card.grade_nine_price != null
      ? card.grade_nine_price - card.ungraded_price - gradingFee
      : null;
  const gradeTenProfit =
    gradedPrice != null ? gradedPrice - card.ungraded_price - gradingFee : null;
  const lowRisk =
    gradeNineProfit != null &&
    gradeNineProfit > 0 &&
    card.grade_ten_price - card.ungraded_price - gradingFee > 0;
  const metrics = [
    {
      label: "Ungraded",
      value: fmt(card.ungraded_price),
      color: "text.primary",
      align: "flex-start",
    },
    {
      label: "PSA 9 profit",
      value: signedMoney(gradeNineProfit),
      color:
        gradeNineProfit == null
          ? "text.secondary"
          : gradeNineProfit > 0
            ? "secondary.main"
            : "error.main",
      align: "center",
    },
    {
      label: `${graderLabel} 10 profit`,
      value: signedMoney(gradeTenProfit),
      color:
        gradeTenProfit == null
          ? "text.secondary"
          : gradeTenProfit > 0
            ? "secondary.main"
            : "error.main",
      align: "flex-end",
    },
  ];

  return (
    <Box
      sx={{
        py: 2,
        borderBottom: `1px solid ${BorderColor}`,
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: { xs: 64, sm: 72 },
            aspectRatio: "5 / 7",
            bgcolor: "#f3f2f0",
            borderRadius: "3px",
            overflow: "hidden",
            flexShrink: 0,
            boxShadow: "0 2px 5px rgba(7, 0, 26, 0.12)",
          }}
        >
          {card.image && (
            <Box
              component="img"
              src={cardImageUrls(card.image).display}
              alt={card.name}
              onClick={() => onImageClick(cardImageUrls(card.image).high)}
              onError={(event: any) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                cursor: "zoom-in",
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="baseline">
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 16,
                    lineHeight: 1.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {card.name}
                </Typography>
                {card.number && (
                  <Typography
                    sx={{ fontSize: 13, lineHeight: 1.35, flexShrink: 0 }}
                  >
                    #{card.number}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {[
                    card.set_name,
                    card.variant ? VARIANT_LABELS[card.variant] : null,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </Typography>
                {card.tcg_player_id > 0 && (
                  <Box
                    component="a"
                    href={tcgPlayerAffiliateUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${card.name} on TCGplayer`}
                    sx={{ display: "inline-flex", flexShrink: 0 }}
                  >
                    <Box
                      component="img"
                      src="/tcg_player.png"
                      alt=""
                      sx={{ height: 11, display: "block" }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            <Stack spacing={0.5} alignItems="flex-end" flexShrink={0}>
              <Typography
                sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700 }}
              >
                {Math.max(0, Math.round(card.sales_volume)).toLocaleString(
                  "en-US",
                )}{" "}
                sold · 24h
              </Typography>
              {lowRisk && <LowRiskBadge />}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              mt: 2,
            }}
          >
            {metrics.map((metric) => (
              <Stack
                key={metric.label}
                spacing={0.25}
                alignItems={metric.align}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {metric.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 14, sm: 16 },
                    lineHeight: 1.25,
                    fontWeight: 800,
                    color: metric.color,
                    whiteSpace: "nowrap",
                  }}
                >
                  {metric.value}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

function getInitialParams() {
  const params = new URLSearchParams(window.location.search);
  const hasPresetFilterParams = [
    "min_ungraded",
    "max_ungraded",
    "min_release_year",
    "max_release_year",
    "min_volume",
  ].some((key) => params.has(key));
  const topPlays =
    params.get("top_plays") === "true" ||
    (params.get("top_plays") !== "false" && !hasPresetFilterParams);
  return {
    text: params.get("text") || "",
    pokemon: params.getAll("pokemon"),
    sets: params.getAll("set_name"),
    minUngraded:
      params.get("min_ungraded") ??
      (topPlays ? TOP_PLAYS_DEFAULTS.minUngraded : ""),
    maxUngraded:
      params.get("max_ungraded") ??
      (topPlays ? TOP_PLAYS_DEFAULTS.maxUngraded : ""),
    minReleaseYear:
      params.get("min_release_year") ??
      (topPlays ? TOP_PLAYS_DEFAULTS.minReleaseYear : ""),
    maxReleaseYear:
      params.get("max_release_year") ??
      (topPlays ? TOP_PLAYS_DEFAULTS.maxReleaseYear : ""),
    minVolumeFilter:
      parseInt(params.get("min_volume") || "0", 10) ||
      (topPlays ? TOP_PLAYS_DEFAULTS.minVolumeFilter : 0),
    topPlays,
    lowRisk: params.get("safe_plays") === "true",
    fee: params.get("fee") || "24.99",
    customFee: params.get("custom_fee") || "",
    grader: params.get("grader") || "psa",
    sort: (params.get("sort") === "percent" ? "percent" : "spread") as
      "spread" | "percent",
    page: parseInt(params.get("page") || "0", 10) || 0,
  };
}

// ── MultiSelectFilter ──────────────────────────────────────────────────────
interface MultiSelectFilterProps {
  label: string;
  selected: string[];
  onSelect: (values: string[]) => void;
  fetchOptions: (q: string) => Promise<OptionItem[]>;
  placeholder?: string;
}

function MultiSelectFilter({
  label,
  selected,
  onSelect,
  fetchOptions,
  placeholder,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchOptions(query);
        // Move already-selected items to top
        const selectedSet = new Set(selected);
        const top = results.filter((o) => selectedSet.has(o.name));
        const rest = results.filter((o) => !selectedSet.has(o.name));
        setOptions([...top, ...rest]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, fetchOptions, selected]);

  const toggle = (name: string) => {
    if (selected.includes(name)) {
      onSelect(selected.filter((s) => s !== name));
    } else {
      onSelect([name, ...selected]);
    }
  };

  const removeChip = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(selected.filter((s) => s !== name));
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", minWidth: 200 }}>
        {/* Trigger */}
        <Box
          ref={anchorRef}
          onClick={() => setOpen((o) => !o)}
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            minHeight: 40,
            px: 1.5,
            py: 0.75,
            border: `1px solid ${open ? "#07001a" : BorderColor}`,
            borderRadius: 2,
            bgcolor: "white",
            cursor: "pointer",
            transition: "border-color 0.15s",
            "&:hover": { borderColor: "#9CA3AF" },
          }}
        >
          {selected.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1, userSelect: "none" }}
            >
              {label}
            </Typography>
          )}
          {selected.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onDelete={(e) => removeChip(s, e)}
              deleteIcon={<CloseIcon style={{ fontSize: 12 }} />}
              sx={{
                height: 26,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: "#07001a",
                color: "white",
                "& .MuiChip-label": { px: 1.5 },
                "& .MuiChip-deleteIcon": {
                  fontSize: 10,
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "white" },
                },
              }}
            />
          ))}
          <KeyboardArrowDownIcon
            sx={{
              ml: "auto",
              fontSize: 18,
              color: "text.secondary",
              flexShrink: 0,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          />
        </Box>

        {/* Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth ?? 240 }}
        >
          <Paper
            elevation={4}
            sx={{
              border: `1px solid ${BorderColor}`,
              borderRadius: "12px",
              mt: 0.5,
              overflow: "hidden",
            }}
          >
            {/* Search input */}
            <Box sx={{ p: 1, borderBottom: `1px solid ${BorderColor}` }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder={placeholder || `Search ${label.toLowerCase()}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: "white" }}
              />
            </Box>

            {/* Options list */}
            <List
              dense
              disablePadding
              sx={{ maxHeight: 260, overflowY: "auto" }}
            >
              {loading ? (
                <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={20} />
                </Box>
              ) : options.length === 0 ? (
                <Box sx={{ py: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No results
                  </Typography>
                </Box>
              ) : (
                options.map((opt) => {
                  const isSelected = selected.includes(opt.name);
                  return (
                    <ListItemButton
                      key={opt.name}
                      onClick={() => toggle(opt.name)}
                      selected={isSelected}
                      sx={{
                        py: 0.75,
                        "&.Mui-selected": { bgcolor: alpha("#07001a", 0.07) },
                        "&.Mui-selected:hover": {
                          bgcolor: alpha("#111827", 0.1),
                        },
                      }}
                    >
                      <ListItemText
                        primary={opt.name}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: isSelected ? 700 : 400,
                        }}
                      />
                      {isSelected && (
                        <CloseIcon
                          sx={{ fontSize: 14, color: "text.secondary", ml: 1 }}
                        />
                      )}
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

// ── Era Filter ────────────────────────────────────────────────────────────
const ERA_OPTIONS = [
  { label: "All Eras", value: "", min: "", max: "" },
  { label: "Vintage (1999–2006)", value: "vintage", min: "1999", max: "2006" },
  { label: "Mid-era (2007–2013)", value: "mid", min: "2007", max: "2013" },
  { label: "Modern (2014–2019)", value: "modern", min: "2014", max: "2019" },
  { label: "Ultra-modern (2019+)", value: "ultra", min: "2019", max: "" },
];

function EraFilter({
  minYear,
  maxYear,
  onSelect,
}: {
  minYear: string;
  maxYear: string;
  onSelect: (min: string, max: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const activeEra = ERA_OPTIONS.find(
    (e) => e.min === minYear && e.max === maxYear,
  );
  const displayLabel = activeEra ? activeEra.label : "All Eras";
  const hasSelection = !!activeEra && activeEra.value !== "";

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", minWidth: 200 }}>
        <Box
          ref={anchorRef}
          onClick={() => setOpen((o) => !o)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minHeight: 40,
            px: 1.5,
            py: 0.75,
            border: `1px solid ${open ? "#07001a" : BorderColor}`,
            borderRadius: 2,
            bgcolor: "white",
            cursor: "pointer",
            transition: "border-color 0.15s",
            "&:hover": { borderColor: "#9CA3AF" },
          }}
        >
          {hasSelection ? (
            <Chip
              label={displayLabel}
              size="small"
              onDelete={(e) => {
                e.stopPropagation();
                onSelect("", "");
              }}
              deleteIcon={<CloseIcon style={{ fontSize: 12 }} />}
              sx={{
                height: 26,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: "#07001a",
                color: "white",
                "& .MuiChip-label": { px: 1.5 },
                "& .MuiChip-deleteIcon": {
                  fontSize: 10,
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "white" },
                },
              }}
            />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1, userSelect: "none" }}
            >
              All Eras
            </Typography>
          )}
          <KeyboardArrowDownIcon
            sx={{
              ml: "auto",
              fontSize: 18,
              color: "text.secondary",
              flexShrink: 0,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          />
        </Box>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth ?? 240 }}
        >
          <Paper
            elevation={4}
            sx={{
              border: `1px solid ${BorderColor}`,
              borderRadius: "12px",
              mt: 0.5,
              overflow: "hidden",
            }}
          >
            <List
              dense
              disablePadding
              sx={{ maxHeight: 260, overflowY: "auto" }}
            >
              {ERA_OPTIONS.map((opt) => {
                const isSelected = opt.min === minYear && opt.max === maxYear;
                return (
                  <ListItemButton
                    key={opt.value}
                    onClick={() => {
                      onSelect(opt.min, opt.max);
                      setOpen(false);
                    }}
                    selected={isSelected}
                    sx={{
                      py: 0.75,
                      "&.Mui-selected": { bgcolor: alpha("#07001a", 0.07) },
                      "&.Mui-selected:hover": {
                        bgcolor: alpha("#07001a", 0.1),
                      },
                    }}
                  >
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: isSelected ? 700 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Arbitrage() {
  const [initParams] = useState(getInitialParams);
  const [cards, setCards] = useState<ArbitrageCard[]>([]);
  const [volumeBaseline, setVolumeBaseline] = useState<ArbitrageCard[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(initParams.page * LIMIT);

  const [feeValue, setFeeValue] = useState<string>(initParams.fee);
  const [customFee, setCustomFee] = useState<string>(initParams.customFee);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [gradingProvider, setGradingProvider] = useState(initParams.grader);
  const [graderOpen, setGraderOpen] = useState(false);
  const graderAnchorRef = useRef<HTMLDivElement>(null);
  const [feeOpen, setFeeOpen] = useState(false);
  const feeAnchorRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState<"spread" | "percent">(initParams.sort);
  const [sortOpen, setSortOpen] = useState(false);
  const sortAnchorRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [topPlays, setTopPlays] = useState(initParams.topPlays);
  const [lowRisk, setLowRisk] = useState(initParams.lowRisk);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Filters
  const [selectedPokemon, setSelectedPokemon] = useState<string[]>(
    initParams.pokemon,
  );
  const [selectedSets, setSelectedSets] = useState<string[]>(initParams.sets);
  const [minUngraded, setMinUngraded] = useState(initParams.minUngraded);
  const [maxUngraded, setMaxUngraded] = useState(initParams.maxUngraded);
  const [textQuery, setTextQuery] = useState(initParams.text);
  const [minReleaseYear, setMinReleaseYear] = useState(
    initParams.minReleaseYear,
  );
  const [maxReleaseYear, setMaxReleaseYear] = useState(
    initParams.maxReleaseYear,
  );
  const [minVolumeFilter, setMinVolumeFilter] = useState(
    initParams.minVolumeFilter,
  );

  // Committed (debounced) filter state that triggers API calls
  const [committed, setCommitted] = useState({
    pokemon: initParams.pokemon,
    sets: initParams.sets,
    minUngraded: initParams.minUngraded,
    maxUngraded: initParams.maxUngraded,
    text: initParams.text,
    minReleaseYear: initParams.minReleaseYear,
    maxReleaseYear: initParams.maxReleaseYear,
    minVolumeFilter: initParams.minVolumeFilter,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeFee =
    feeValue === "custom" ? parseFloat(customFee) || 0 : parseFloat(feeValue);
  const activeProvider =
    GRADING_PROVIDERS.find((p) => p.key === gradingProvider) ||
    GRADING_PROVIDERS[0];
  const getGradedPrice = (card: ArbitrageCard): number | null => {
    const v = card[activeProvider.field];
    return v != null ? v : null;
  };
  const toggleTopPlays = () => {
    if (topPlays) {
      setMinUngraded("");
      setMaxUngraded("");
      setMinReleaseYear("");
      setMaxReleaseYear("");
      setMinVolumeFilter(0);
      setTopPlays(false);
      return;
    }
    setMinUngraded(TOP_PLAYS_DEFAULTS.minUngraded);
    setMaxUngraded(TOP_PLAYS_DEFAULTS.maxUngraded);
    setMinReleaseYear(TOP_PLAYS_DEFAULTS.minReleaseYear);
    setMaxReleaseYear(TOP_PLAYS_DEFAULTS.maxReleaseYear);
    setMinVolumeFilter(TOP_PLAYS_DEFAULTS.minVolumeFilter);
    setSelectedPokemon([]);
    setSelectedSets([]);
    setGradingProvider("psa");
    setLowRisk(false);
    setTopPlays(true);
  };

  const skipInitialDebounce = useRef(true);
  useEffect(() => {
    if (skipInitialDebounce.current) {
      skipInitialDebounce.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCommitted({
        pokemon: selectedPokemon,
        sets: selectedSets,
        minUngraded,
        maxUngraded,
        text: textQuery,
        minReleaseYear,
        maxReleaseYear,
        minVolumeFilter,
      });
      setCurrentOffset(0);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    selectedPokemon,
    selectedSets,
    minUngraded,
    maxUngraded,
    textQuery,
    minReleaseYear,
    maxReleaseYear,
    minVolumeFilter,
  ]);

  const buildUrl = useCallback(
    (off: number) => {
      const params = new URLSearchParams();
      params.set("limit", String(LIMIT));
      params.set("offset", String(off));
      committed.pokemon.forEach((p) => params.append("pokemon", p));
      committed.sets.forEach((s) => params.append("set_name", s));
      if (committed.minUngraded)
        params.set("min_ungraded", committed.minUngraded);
      if (committed.maxUngraded)
        params.set("max_ungraded", committed.maxUngraded);
      if (committed.text) params.set("text", committed.text);
      if (committed.minReleaseYear)
        params.set("min_release_year", committed.minReleaseYear);
      if (committed.maxReleaseYear)
        params.set("max_release_year", committed.maxReleaseYear);
      if (sortBy !== "spread") params.set("sort", sortBy);
      if (gradingProvider !== "psa") params.set("grader", gradingProvider);
      return `${API_BASE}/api/v1/public/arbitrage?${params.toString()}`;
    },
    [committed, sortBy, gradingProvider],
  );

  const fetchCards = useCallback(
    async (off: number) => {
      setLoading(true);
      setError(null);
      try {
        const [res, baselineRes] = await Promise.all([
          fetch(buildUrl(off)),
          off === 0 ? Promise.resolve(null) : fetch(buildUrl(0)),
        ]);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: ArbitrageResponse = await res.json();
        let baselineCards = data.cards || [];
        if (baselineRes) {
          if (!baselineRes.ok) throw new Error(`HTTP ${baselineRes.status}`);
          const baselineData: ArbitrageResponse = await baselineRes.json();
          baselineCards = baselineData.cards || [];
        }
        // All state updates in one batch — no intermediate empty frame
        setCards(data.cards || []);
        setVolumeBaseline(
          baselineCards.slice(0, APP_VOLUME_BASELINE_SIZE),
        );
        setTotal(data.total);
        setLoading(false);
      } catch (e: any) {
        setError(e.message || "Failed to load data");
        setLoading(false);
      }
    },
    [buildUrl],
  );

  const fetchOffsetRef = useRef(initParams.page * LIMIT);
  useEffect(() => {
    fetchCards(fetchOffsetRef.current);
    if (fetchOffsetRef.current === 0) setCurrentOffset(0);
    fetchOffsetRef.current = 0;
  }, [fetchCards]);

  // Sync all filter state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (committed.text) params.set("text", committed.text);
    committed.pokemon.forEach((p) => params.append("pokemon", p));
    committed.sets.forEach((s) => params.append("set_name", s));
    if (committed.minUngraded)
      params.set("min_ungraded", committed.minUngraded);
    if (committed.maxUngraded)
      params.set("max_ungraded", committed.maxUngraded);
    if (committed.minReleaseYear)
      params.set("min_release_year", committed.minReleaseYear);
    if (committed.maxReleaseYear)
      params.set("max_release_year", committed.maxReleaseYear);
    if (committed.minVolumeFilter > 0)
      params.set("min_volume", String(committed.minVolumeFilter));
    if (feeValue !== "24.99") params.set("fee", feeValue);
    if (feeValue === "custom" && customFee) params.set("custom_fee", customFee);
    if (gradingProvider !== "psa") params.set("grader", gradingProvider);
    if (sortBy !== "spread") params.set("sort", sortBy);
    if (topPlays) params.set("top_plays", "true");
    if (lowRisk) params.set("safe_plays", "true");
    const page = Math.floor(currentOffset / LIMIT);
    if (page > 0) params.set("page", String(page));
    const qs = params.toString();
    const newUrl = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    window.history.replaceState(null, "", newUrl);
  }, [
    committed,
    feeValue,
    customFee,
    gradingProvider,
    sortBy,
    topPlays,
    lowRisk,
    currentOffset,
  ]);

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(currentOffset / LIMIT);
  const getVolumeLevel = useMemo(
    () => createVolumeLevelGetter(volumeBaseline),
    [volumeBaseline],
  );
  const visibleCards = useMemo(() => {
    let filtered = cards;
    if (lowRisk) {
      filtered = filtered.filter((card) => {
        if (card.grade_nine_price == null) return false;
        const psaNineProfit =
          card.grade_nine_price - card.ungraded_price - activeFee;
        const psaTenProfit =
          card.grade_ten_price - card.ungraded_price - activeFee;
        return psaNineProfit > 0 && psaTenProfit > 0;
      });
    }
    if (minVolumeFilter > 1) {
      filtered = filtered.filter(
        (card) => getVolumeLevel(card.sales_volume) >= minVolumeFilter,
      );
    }
    return filtered;
  }, [cards, lowRisk, activeFee, minVolumeFilter, getVolumeLevel]);

  const goToPage = (page: number) => {
    const off = page * LIMIT;
    setCurrentOffset(off);
    fetchCards(off);
  };

  const fetchSets = useCallback(async (q: string): Promise<OptionItem[]> => {
    const res = await fetch(
      `${API_BASE}/api/v1/public/arbitrage/sets?q=${encodeURIComponent(q)}&limit=30`,
    );
    if (!res.ok) return [];
    return res.json();
  }, []);

  const fetchPokemon = useCallback(async (q: string): Promise<OptionItem[]> => {
    const res = await fetch(
      `${API_BASE}/api/v1/public/arbitrage/pokemon?q=${encodeURIComponent(q)}&limit=30`,
    );
    if (!res.ok) return [];
    return res.json();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <Container maxWidth="lg" sx={{ py: 2, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              py: 1.5,
            }}
          >
            <Box
              component="a"
              href="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src="/logo-full.svg"
                alt="Evo"
                sx={{
                  height: 22,
                  width: "auto",
                  filter: "invert(1)",
                  cursor: "pointer",
                }}
              />
            </Box>
            <Button
              size="small"
              href="/"
              sx={{
                color: "#07001a",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "12px",
                px: 1.5,
                py: 0.5,
                bgcolor: "white",
                boxShadow: "0px 3px 3.5px 0px rgba(0,0,0,0.05)",
                "&:hover": { bgcolor: "#f3f2f0" },
              }}
            >
              ← Back to home
            </Button>
          </Box>
          <Stack spacing={0.5} sx={{ mb: 3, mt: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                  <path
                    d={toolsSvgPaths.p2e37d000}
                    stroke="#44B300"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="5"
                  />
                  <path
                    d={toolsSvgPaths.p37d87880}
                    stroke="#44B300"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="5"
                  />
                  <path
                    d="M24 13V16M24 35V32"
                    stroke="#44B300"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="5"
                  />
                </svg>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  color: "text.primary",
                  letterSpacing: "-0.64px",
                }}
              >
                Grading plays
              </Typography>
            </Stack>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              Sort cards by expected grading profit to find the most
              profitable cards to grade
            </Typography>
            {total > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ pt: 0.5 }}
              >
                {total.toLocaleString()} cards · updated daily
              </Typography>
            )}
          </Stack>

          {/* Mobile search + filter toggle */}
          {isMobile && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search card name…"
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                }}
              />
              <Button
                variant="outlined"
                onClick={() => setShowFilters((f) => !f)}
                sx={{
                  minWidth: 44,
                  px: 1,
                  borderColor: BorderColor,
                  color: "text.primary",
                  borderRadius: "12px",
                  bgcolor: "white",
                  boxShadow: "0px 3px 3.5px 0px rgba(0,0,0,0.05)",
                }}
              >
                <FilterListIcon fontSize="small" />
              </Button>
            </Stack>
          )}

          {/* Filters */}
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${BorderColor}`,
              borderRadius: "12px",
              p: 2,
              mb: 3,
              boxShadow: "0px 3px 3.5px 0px rgba(0,0,0,0.05)",
              display: isMobile && !showFilters ? "none" : "block",
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant={topPlays ? "contained" : "outlined"}
                  onClick={toggleTopPlays}
                  sx={{ borderColor: BorderColor, borderRadius: "8px" }}
                >
                  Top plays
                </Button>
                <Button
                  size="small"
                  variant={lowRisk ? "contained" : "outlined"}
                  startIcon={<ShieldOutlinedIcon />}
                  onClick={() => setLowRisk((value) => !value)}
                  sx={{
                    borderColor: lowRisk ? "secondary.main" : BorderColor,
                    borderRadius: "8px",
                    bgcolor: lowRisk ? "secondary.main" : "white",
                    "&:hover": {
                      bgcolor: lowRisk ? "#12813d" : "#f3f2f0",
                    },
                  }}
                >
                  Low risk
                </Button>
              </Stack>

              <Divider />

              {/* Row 1: search, pokemon, set */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "flex-start" }}
              >
                <Box sx={{ flex: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Search
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search card name…"
                    value={textQuery}
                    onChange={(e) => setTextQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Pokémon
                  </Typography>
                  <MultiSelectFilter
                    label="All Pokémon"
                    selected={selectedPokemon}
                    onSelect={setSelectedPokemon}
                    fetchOptions={fetchPokemon}
                    placeholder="Search Pokémon…"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Set
                  </Typography>
                  <MultiSelectFilter
                    label="All Sets"
                    selected={selectedSets}
                    onSelect={setSelectedSets}
                    fetchOptions={fetchSets}
                    placeholder="Search sets…"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Era
                  </Typography>
                  <EraFilter
                    minYear={minReleaseYear}
                    maxYear={maxReleaseYear}
                    onSelect={(min, max) => {
                      setMinReleaseYear(min);
                      setMaxReleaseYear(max);
                      setTopPlays(false);
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    alignSelf: "stretch",
                    pt: 2,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setTextQuery("");
                      setSelectedPokemon([]);
                      setSelectedSets([]);
                      setMinUngraded("");
                      setMaxUngraded("");
                      setMinReleaseYear("");
                      setMaxReleaseYear("");
                      setMinVolumeFilter(0);
                      setFeeValue("24.99");
                      setCustomFee("");
                      setGradingProvider("psa");
                      setSortBy("spread");
                      setTopPlays(false);
                      setLowRisk(false);
                    }}
                    sx={{
                      color: "text.secondary",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      minWidth: "auto",
                      px: 1,
                    }}
                  >
                    Clear all
                  </Button>
                </Box>
              </Stack>

              <Divider />

              {/* Row 2: language + price range + fee */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                flexWrap="wrap"
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Ungraded price
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Min"
                      value={minUngraded}
                      onChange={(e) => {
                        setMinUngraded(e.target.value);
                        setTopPlays(false);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      sx={{ width: 100, bgcolor: "white" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      –
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Max"
                      value={maxUngraded}
                      onChange={(e) => {
                        setMaxUngraded(e.target.value);
                        setTopPlays(false);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      sx={{ width: 100, bgcolor: "white" }}
                    />
                  </Stack>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Release year
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Min"
                      value={minReleaseYear}
                      onChange={(e) => {
                        setMinReleaseYear(e.target.value);
                        setTopPlays(false);
                      }}
                      sx={{ width: 80, bgcolor: "white" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      –
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Max"
                      value={maxReleaseYear}
                      onChange={(e) => {
                        setMaxReleaseYear(e.target.value);
                        setTopPlays(false);
                      }}
                      sx={{ width: 80, bgcolor: "white" }}
                    />
                  </Stack>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Minimum volume
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {[
                      { value: 0, label: "All" },
                      { value: 1, label: "Low" },
                      { value: 2, label: "Medium" },
                      { value: 4, label: "High" },
                    ].map((option) => {
                      const active = minVolumeFilter === option.value;
                      return (
                        <Button
                          key={option.value}
                          size="small"
                          variant={active ? "contained" : "outlined"}
                          onClick={() => {
                            setMinVolumeFilter(option.value);
                            setTopPlays(false);
                          }}
                          sx={{
                            minWidth: "auto",
                            height: 34,
                            px: 1.25,
                            borderRadius: "8px",
                            borderColor: active ? "#07001a" : BorderColor,
                            fontSize: 11,
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Grading company
                  </Typography>
                  <ClickAwayListener onClickAway={() => setGraderOpen(false)}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        ref={graderAnchorRef}
                        onClick={() => setGraderOpen((o) => !o)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          minHeight: 40,
                          px: 1.5,
                          py: 0.75,
                          border: `1px solid ${graderOpen ? "#07001a" : BorderColor}`,
                          borderRadius: 2,
                          bgcolor: "white",
                          cursor: "pointer",
                          transition: "border-color 0.15s",
                          "&:hover": { borderColor: "#9CA3AF" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, userSelect: "none", fontWeight: 500 }}
                        >
                          {activeProvider.label}
                        </Typography>
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            transform: graderOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      </Box>
                      <Popper
                        open={graderOpen}
                        anchorEl={graderAnchorRef.current}
                        placement="bottom-start"
                        style={{ zIndex: 1300 }}
                      >
                        <Paper
                          elevation={4}
                          sx={{
                            mt: 0.5,
                            minWidth: graderAnchorRef.current?.offsetWidth,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <List disablePadding dense>
                            {GRADING_PROVIDERS.map((p) => (
                              <ListItemButton
                                key={p.key}
                                selected={gradingProvider === p.key}
                                onClick={() => {
                                  setGradingProvider(p.key);
                                  setGraderOpen(false);
                                }}
                                sx={{ py: 1 }}
                              >
                                <ListItemText
                                  primary={p.label}
                                  primaryTypographyProps={{
                                    variant: "body2",
                                    fontWeight:
                                      gradingProvider === p.key ? 700 : 400,
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      </Popper>
                    </Box>
                  </ClickAwayListener>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Sort by
                  </Typography>
                  <ClickAwayListener onClickAway={() => setSortOpen(false)}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        ref={sortAnchorRef}
                        onClick={() => setSortOpen((o) => !o)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          minHeight: 40,
                          px: 1.5,
                          py: 0.75,
                          border: `1px solid ${sortOpen ? "#07001a" : BorderColor}`,
                          borderRadius: 2,
                          bgcolor: "white",
                          cursor: "pointer",
                          transition: "border-color 0.15s",
                          "&:hover": { borderColor: "#9CA3AF" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            userSelect: "none",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sortBy === "spread" ? "Profit" : "Returns"}
                        </Typography>
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            transform: sortOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      </Box>
                      <Popper
                        open={sortOpen}
                        anchorEl={sortAnchorRef.current}
                        placement="bottom-start"
                        style={{ zIndex: 1300 }}
                      >
                        <Paper
                          elevation={4}
                          sx={{
                            mt: 0.5,
                            minWidth: sortAnchorRef.current?.offsetWidth,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <List disablePadding dense>
                            <ListItemButton
                              selected={sortBy === "spread"}
                              onClick={() => {
                                setSortBy("spread");
                                setSortOpen(false);
                              }}
                              sx={{ py: 1 }}
                            >
                              <ListItemText
                                primary="Profit"
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontWeight: sortBy === "spread" ? 700 : 400,
                                }}
                              />
                            </ListItemButton>
                            <ListItemButton
                              selected={sortBy === "percent"}
                              onClick={() => {
                                setSortBy("percent");
                                setSortOpen(false);
                              }}
                              sx={{ py: 1 }}
                            >
                              <ListItemText
                                primary="Returns"
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontWeight: sortBy === "percent" ? 700 : 400,
                                }}
                              />
                            </ListItemButton>
                          </List>
                        </Paper>
                      </Popper>
                    </Box>
                  </ClickAwayListener>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Grading fee
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {feeValue === "custom" ? (
                      <TextField
                        size="small"
                        placeholder="Amount"
                        value={customFee}
                        onChange={(e) => setCustomFee(e.target.value)}
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        sx={{
                          width: 130,
                          bgcolor: "white",
                          "& .MuiOutlinedInput-root": { height: 40 },
                        }}
                      />
                    ) : (
                      <ClickAwayListener onClickAway={() => setFeeOpen(false)}>
                        <Box sx={{ position: "relative" }}>
                          <Box
                            ref={feeAnchorRef}
                            onClick={() => setFeeOpen((o) => !o)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              minHeight: 40,
                              px: 1.5,
                              py: 0.75,
                              border: `1px solid ${feeOpen ? "#07001a" : BorderColor}`,
                              borderRadius: 2,
                              bgcolor: "white",
                              cursor: "pointer",
                              transition: "border-color 0.15s",
                              "&:hover": { borderColor: "#9CA3AF" },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                userSelect: "none",
                                fontWeight: 500,
                              }}
                            >
                              ${feeValue}
                            </Typography>
                            <KeyboardArrowDownIcon
                              sx={{
                                fontSize: 18,
                                color: "text.secondary",
                                transform: feeOpen ? "rotate(180deg)" : "none",
                                transition: "transform 0.2s",
                              }}
                            />
                          </Box>
                          <Popper
                            open={feeOpen}
                            anchorEl={feeAnchorRef.current}
                            placement="bottom-start"
                            style={{ zIndex: 1300 }}
                          >
                            <Paper
                              elevation={4}
                              sx={{
                                mt: 0.5,
                                minWidth: feeAnchorRef.current?.offsetWidth,
                                borderRadius: 2,
                                overflow: "hidden",
                              }}
                            >
                              <List disablePadding dense>
                                {PRESET_FEES.map((f) => (
                                  <ListItemButton
                                    key={f.value}
                                    selected={feeValue === String(f.value)}
                                    onClick={() => {
                                      setFeeValue(String(f.value));
                                      setFeeOpen(false);
                                    }}
                                    sx={{ py: 1 }}
                                  >
                                    <ListItemText
                                      primary={f.label}
                                      primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight:
                                          feeValue === String(f.value)
                                            ? 700
                                            : 400,
                                      }}
                                    />
                                  </ListItemButton>
                                ))}
                                <ListItemButton
                                  onClick={() => {
                                    setFeeValue("custom");
                                    setFeeOpen(false);
                                  }}
                                  sx={{ py: 1 }}
                                >
                                  <ListItemText
                                    primary="Custom…"
                                    primaryTypographyProps={{
                                      variant: "body2",
                                      fontWeight: 400,
                                    }}
                                  />
                                </ListItemButton>
                              </List>
                            </Paper>
                          </Popper>
                        </Box>
                      </ClickAwayListener>
                    )}
                    {feeValue === "custom" && (
                      <Box
                        onClick={() => setFeeValue("24.99")}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#07001a",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#1a1330" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16, color: "#fff" }} />
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Error */}
          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "#FEF2F2",
                borderRadius: 2,
                border: "1px solid #FCA5A5",
              }}
            >
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {/* Plays list */}
          {loading && cards.length === 0 ? (
            <SkeletonPlayRows />
          ) : visibleCards.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid rgba(0, 6, 20, 0.08)",
                borderRadius: "12px",
                py: 6,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No cards found
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid rgba(0, 6, 20, 0.08)",
                borderRadius: "12px",
                px: { xs: 1.5, sm: 2 },
                bgcolor: "white",
                overflow: "hidden",
                boxShadow: "0px 3px 3.5px 0px rgba(0,0,0,0.05)",
              }}
            >
              {visibleCards.map((card) => (
                <PlayRow
                  key={card.id}
                  card={card}
                  gradedPrice={getGradedPrice(card)}
                  graderLabel={activeProvider.label}
                  gradingFee={activeFee}
                  onImageClick={setLightboxSrc}
                />
              ))}
            </Paper>
          )}

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {total > 0
                ? `${(currentPage * LIMIT + 1).toLocaleString()}–${Math.min((currentPage + 1) * LIMIT, total).toLocaleString()} of ${total.toLocaleString()}`
                : ""}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === 0 || loading}
                onClick={() => goToPage(currentPage - 1)}
                sx={{
                  borderColor: BorderColor,
                  color: "text.primary",
                  borderRadius: 999,
                  minWidth: 44,
                  px: 2,
                  py: 1,
                }}
              >
                ←
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                {loading ? (
                  <CircularProgress size={14} />
                ) : (
                  `${currentPage + 1} / ${totalPages || 1}`
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage >= totalPages - 1 || loading}
                onClick={() => goToPage(currentPage + 1)}
                sx={{
                  borderColor: BorderColor,
                  color: "text.primary",
                  borderRadius: 999,
                  minWidth: 44,
                  px: 2,
                  py: 1,
                }}
              >
                →
              </Button>
            </Stack>
          </Box>

          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontSize: 12,
              }}
            >
              Profit = selected grader 10 price - ungraded price - grading fee.
              As a TCGPlayer affiliate, Evo may earn from qualifying purchases.
            </Typography>
          </Box>
        </Container>

        {/* Footer */}
        <div className="bg-[#ff381a] relative shrink-0 w-full mt-8">
          <div className="flex flex-col items-center justify-center size-full">
            <div className="content-stretch flex flex-col items-center justify-center px-[24px] py-[24px] relative w-full box-border">
              <div className="content-stretch flex flex-col sm:flex-row items-center justify-between max-w-[1440px] relative shrink-0 w-full gap-[12px]">
                <div className="content-stretch flex flex-wrap items-center relative shrink-0">
                  {/* Evo logo */}
                  <div className="h-[30px] relative shrink-0 w-[105.6px]">
                    <svg
                      className="absolute block size-full"
                      fill="none"
                      preserveAspectRatio="none"
                      viewBox="0 0 105.6 30"
                    >
                      <g>
                        <path d={svgPaths.p17c40c00} fill="white" />
                        <path d={svgPaths.p3f0f4200} fill="white" />
                        <path d={svgPaths.p2e034c00} fill="white" />
                        <path d={svgPaths.p1e156e00} fill="white" />
                        <path d={svgPaths.p15637080} fill="white" />
                      </g>
                    </svg>
                  </div>
                  {/* Discord */}
                  <div
                    className="content-stretch flex gap-[12px] items-center px-[12px] py-[6px] relative rounded-[20px] shrink-0 cursor-pointer hover:opacity-80"
                    onClick={() =>
                      window.open("https://discord.gg/XTxUgUAFJh", "_blank")
                    }
                  >
                    <div className="overflow-clip relative shrink-0 size-[18px]">
                      <div className="absolute inset-[11.13%_0_11.46%_0]">
                        <svg
                          className="absolute block size-full"
                          fill="none"
                          preserveAspectRatio="none"
                          viewBox="0 0 18 13.9338"
                        >
                          <path d={svgPaths.pd84a880} fill="white" />
                        </svg>
                      </div>
                    </div>
                    <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
                      Join our Discord server
                    </p>
                  </div>
                  {/* Email */}
                  <a
                    href="mailto:hello@evograding.com"
                    className="content-stretch flex gap-[12px] items-center px-[12px] py-[6px] relative rounded-[20px] shrink-0 no-underline hover:opacity-80"
                  >
                    <div className="relative shrink-0 size-[18px]">
                      <svg
                        className="absolute block size-full"
                        fill="none"
                        preserveAspectRatio="none"
                        viewBox="0 0 18 18"
                      >
                        <path
                          d={svgPaths.p3ae5cc00}
                          stroke="white"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                        <path
                          d={svgPaths.p3e25a540}
                          stroke="white"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </div>
                    <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
                      hello@evograding.com
                    </p>
                  </a>
                </div>
                <p className="font-['Figtree',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[12px] text-white whitespace-nowrap">
                  &copy; 2026 Evo Collectibles Inc.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Box>

      {/* Lightbox */}
      <Modal
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
        disableAutoFocus
        disableEnforceFocus
      >
        <Box
          onClick={() => setLightboxSrc(null)}
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.85)",
            cursor: "zoom-out",
            outline: "none",
          }}
        >
          {lightboxSrc && (
            <Box
              component="img"
              src={lightboxSrc}
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxHeight: "90vh",
                maxWidth: "90vw",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
      </Modal>
    </ThemeProvider>
  );
}
